import axios from 'axios';
import { Auth } from 'aws-amplify';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
});

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(token) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addSubscriber(callback) {
  refreshSubscribers.push(callback);
}

async function alertAndLogout(message = 'Your session has expired. Please log in again.') {
  try {
    alert(message);
    await Auth.signOut();
    window.location.href = '/login';
  } catch (error) {
    console.error('Error during logout:', error);
    window.location.href = '/login';
  }
}

async function checkSessionValidity() {
  try {
    const session = await Auth.currentSession();
    const refreshToken = session.getRefreshToken();
    const now = Math.floor(Date.now() / 1000);
    const refreshTokenExp = refreshToken.getTokenExp?.() || 0;

    if (refreshTokenExp && now > refreshTokenExp) {
      console.warn('⚠️ Refresh token expired, triggering logout...');
      await alertAndLogout('Your login session has expired. Please log in again to continue.');
      throw new Error('Refresh token expired');
    }

    return session;
  } catch (err) {
    console.warn('⚠️ No valid session found, logging out...');
    await alertAndLogout('Your session has expired or is invalid. Please log in again.');
    throw err;
  }
}

axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const session = await checkSessionValidity();
      const token = session.getIdToken().getJwtToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      try {
        const session = await Auth.currentSession();
        const refreshToken = session.getRefreshToken();
        const now = Math.floor(Date.now() / 1000);
        const refreshTokenExp = refreshToken.getTokenExp?.() || 0;
        if (refreshTokenExp && now > refreshTokenExp) {
          console.warn('⚠️ Refresh token expired during 401 handling. Logging out...');
          await alertAndLogout('Your login session has expired. Please sign in again.');
          return;
        }
      } catch (err) {
        console.warn('⚠️ No valid session on 401, logging out...');
        await alertAndLogout('Your session has expired or is invalid. Please log in again.');
        return;
      }
      if (isRefreshing) {
        return new Promise((resolve) => {
          addSubscriber((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const session = await Auth.currentSession();
        const newToken = session.getIdToken().getJwtToken();
        isRefreshing = false;
        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        console.error('❌ Token refresh failed, logging out...', refreshError);
        await alertAndLogout('Your login session has expired. Please log in again.');
      }
    }

    return Promise.reject(error);
  }
);

const AxiosApiService = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data, config = {}) => axiosInstance.put(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config)
};

export default AxiosApiService;
